/*
 * The loading screen (prototype v2.14–2.17): the seal, small — the gold LZ, a light running round it,
 * a glint across it — and a bar, in index.html itself, so it is there before any code has loaded. The
 * console used to draw nothing while the session resumed: a blank page, for seconds on a phone after
 * a deploy. It goes once the page under it runs smoothly; signing in shows it again for the moment the
 * console takes to open. Only transform and opacity move in it, which the browser animates off the
 * page's own thread, so it stays smooth while the code is read and the console is built under it.
 */

const ID = "lz-boot";

/** The loading screen is on the page. */
export const bootUp = (): boolean => !!document.getElementById(ID);

/** It fades out once it has been up minMs since the page started, and tells the sign-in page (lz-boot-gone). */
export function bootOff(minMs = 1500): void {
  const el = document.getElementById(ID);
  if (!el || el.dataset.off) return;
  el.dataset.off = "1";
  window.setTimeout(
    () => {
      el.classList.add("done");
      window.setTimeout(() => {
        el.classList.add("out");
        window.dispatchEvent(new Event("lz-boot-gone"));
        window.setTimeout(() => el.remove(), 450);
      }, 280);
    },
    Math.max(0, minMs - performance.now()),
  );
}

/**
 * It goes once the page under it runs smoothly: a dozen frames in a row on time, after at least minMs,
 * at most maxMs (v2.17: on a timer, the console, still starting, stuttered as it faded). In a tab that
 * is not on screen no frame is drawn, so the clock lets it go then.
 */
export function bootWhenQuiet(minMs = 600, maxMs = 3000): void {
  const el = document.getElementById(ID);
  if (!el || el.dataset.off) return;
  const t0 = performance.now();
  let last = t0;
  let calm = 0;
  const stop = window.setTimeout(() => bootOff(0), maxMs + 200);
  const f = (now: number) => {
    calm = now - last < 22 ? calm + 1 : 0;
    last = now;
    if ((now - t0 > minMs && calm >= 12) || now - t0 > maxMs) {
      window.clearTimeout(stop);
      bootOff(0);
      return;
    }
    requestAnimationFrame(f);
  };
  requestAnimationFrame(f);
}

/** Shows it again with its own words: 「正在登入…」 while the console opens after signing in. */
export function bootShow(text: string): void {
  document.getElementById(ID)?.remove();
  const el = document.createElement("div");
  el.id = ID;
  el.className = "lzboot lzboot-in";
  el.setAttribute("role", "status");
  el.setAttribute("aria-label", text);
  el.innerHTML =
    '<div class="lzboot-c"><span class="lzboot-seal"><i class="lzboot-orbit"></i><i class="lzboot-mark"><i class="lzboot-glint"></i></i></span>' +
    '<i class="lzboot-word"></i><span class="lzboot-bar"><i></i></span><span class="lzboot-t"></span></div>';
  el.querySelector(".lzboot-t")!.textContent = text;
  document.body.appendChild(el);
}
